<template>
  <div class="payment-link-step-installments">
    <!-- Only credit card selected - can configure installments -->
    <template v-if="canAllowInstallments">
      <div class="step-description">
        <p>{{ t('Deseja permitir que o cliente parcele o pagamento?') }}</p>
      </div>

      <div class="charge-type-selector">
        <buttons-list
          data-testid="payment-links-step-charge-type-input"
          :placeholder="t('Escolha o tipo')"
          :validation="() => isChargeTypeValid"
          :required="true"
          elem-id="payment_link_charge_type"
          v-model="chargeType"
          :itens-select="chargeTypeOptions">
        </buttons-list>
      </div>

      <div class="installments-config" v-if="chargeType === 'installment'">
        <div class="installments-input">
          <input-number
            data-testid="payment-links-step-installments-input"
            :label="t('Número máximo de parcelas')"
            :placeholder="t('Ex: 3')"
            :validation="() => isInstallmentsValid"
            :required="true"
            :min="2"
            :max="12"
            elem-id="payment_link_installments"
            v-model="installments">
          </input-number>
        </div>

        <div class="installments-preview" v-if="installments >= 2 && amount > 0">
          <span class="preview-label">{{ t('Valor por parcela') }}:</span>
          <span class="preview-value">{{ installments }}x de {{ formatCurrency(amount / installments / 100) }}</span>
        </div>
      </div>
    </template>

    <!-- Other payment methods selected - installments not available -->
    <template v-else>
      <div class="step-description">
        <p>{{ t('Configuração de parcelamento') }}</p>
      </div>

      <div class="installments-unavailable">
        <i class="pi pi-info-circle"></i>
        <div class="unavailable-content">
          <strong>{{ t('Parcelamento não disponível') }}</strong>
          <p>{{ t('O parcelamento só está disponível quando apenas Cartão de Crédito está selecionado como forma de pagamento.') }}</p>
          <p class="hint">{{ t('Se desejar oferecer parcelamento, volte ao passo anterior e selecione apenas Cartão de Crédito.') }}</p>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

interface ChargeTypeOption {
  ID: 'single' | 'installment';
  Name: string;
}

interface PaymentLinkFormData {
  amount?: number;
  payment_methods?: string[];
  charge_type?: 'single' | 'installment';
  installments?: number;
  [key: string]: unknown;
}

const { t } = useI18n();

const chargeTypeOptions = ref<ChargeTypeOption[]>([
  { ID: 'single', Name: t('Pagamento único (à vista)') },
  { ID: 'installment', Name: t('Permitir parcelamento') }
]);

const props = defineProps<{
  modelValue: PaymentLinkFormData;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: PaymentLinkFormData): void;
}>();

// Computed values with 2-way binding
const chargeType = computed<'single' | 'installment'>({
  get: () => props.modelValue.charge_type || 'single',
  set: (value: 'single' | 'installment') => {
    const updates: Partial<PaymentLinkFormData> = { charge_type: value };
    if (value === 'single') {
      updates.installments = 1;
    } else if (value === 'installment' && (props.modelValue.installments || 1) < 2) {
      updates.installments = 3; // Default to 3 installments
    }
    emit('update:modelValue', { ...props.modelValue, ...updates });
  }
});

const installments = computed<number>({
  get: () => props.modelValue.installments || 1,
  set: (value: number) => {
    emit('update:modelValue', { ...props.modelValue, installments: value });
  }
});

const amount = computed<number>(() => props.modelValue.amount || 0);

// Check if installments can be allowed (only credit card selected)
const canAllowInstallments = computed<boolean>(() => {
  const methods = props.modelValue.payment_methods || [];
  return methods.length === 1 && methods[0] === 'credit_card';
});

// Validation
const isChargeTypeValid = computed<boolean>(() => {
  return !!chargeType.value;
});

const isInstallmentsValid = computed<boolean>(() => {
  if (chargeType.value !== 'installment') return true;
  return installments.value >= 2 && installments.value <= 12;
});

const isValid = computed<boolean>(() => {
  if (!canAllowInstallments.value) return true; // Always valid if installments not available
  return isChargeTypeValid.value && isInstallmentsValid.value;
});

// Methods
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0);
};

// Reset to single payment if methods change and credit card is no longer exclusive
watch(() => props.modelValue.payment_methods, (newMethods) => {
  const methods = newMethods || [];
  const isOnlyCreditCard = methods.length === 1 && methods[0] === 'credit_card';

  if (!isOnlyCreditCard && chargeType.value === 'installment') {
    emit('update:modelValue', {
      ...props.modelValue,
      charge_type: 'single',
      installments: 1
    });
  }
}, { deep: true });

// Expose validation for wizard
defineExpose({
  isValid,
  canAllowInstallments
});
</script>

<style scoped>
.payment-link-step-installments {
  padding: 1rem 0;
}

.step-description {
  margin-bottom: 1.5rem;
  color: var(--text-color-secondary);
}

.step-description p {
  margin: 0;
}

.charge-type-selector {
  margin-bottom: 1.5rem;
}

.installments-config {
  margin-top: 1.5rem;
}

.installments-input {
  max-width: 250px;
}

.installments-preview {
  margin-top: 1rem;
  padding: 1rem;
  background-color: var(--surface-ground);
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.preview-label {
  color: var(--text-color-secondary);
  font-weight: 500;
}

.preview-value {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--primary-color);
}

.installments-unavailable {
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  background-color: var(--surface-ground);
  border-radius: 8px;
  border-left: 4px solid var(--blue-500);
}

.installments-unavailable i {
  font-size: 1.5rem;
  color: var(--blue-500);
  flex-shrink: 0;
}

.unavailable-content {
  flex: 1;
}

.unavailable-content strong {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--text-color);
}

.unavailable-content p {
  margin: 0 0 0.5rem 0;
  color: var(--text-color-secondary);
  font-size: 0.875rem;
}

.unavailable-content .hint {
  color: var(--blue-600);
  font-style: italic;
}
</style>
